import { AudioManager } from '../audio/audio';
import { GAME, VIEW_H, VIEW_W, type Options } from '../config/config';
import { DEFAULT_BINDINGS, InputManager, type Action } from '../core/input';
import { Loop } from '../core/loop';
import { Save, cloneBindings } from '../core/storage';
import { buildSprites } from '../gfx/sprites';
import { Renderer } from '../gfx/renderer';
import { Hud } from '../ui/hud';
import { Overlay, type Screen } from '../ui/overlay';
import { TouchControls } from '../ui/touch';
import type { LevelDef } from '../world/leveldata';
import { validateLevel } from '../world/leveldata';
import { Level, type LevelOptions, type Session } from './level';
import { PLAYER } from '../config/config';

export type GameState = 'title' | 'play' | 'pause' | 'settings' | 'gameover' | 'clear';

/**
 * Top-level scene manager: owns the loop, the shared services (input / audio / renderer)
 * and the current Level, and drives the menu flow.
 */
export class Game {
  readonly input = new InputManager();
  readonly audio = new AudioManager();
  readonly save = new Save();
  readonly renderer: Renderer;
  readonly hud = new Hud();
  readonly overlay: Overlay;
  readonly touch: TouchControls;
  private loop: Loop;

  state: GameState = 'title';
  session: Session = { score: 0, coins: 0, lives: PLAYER.startLives };
  levelIndex = 0;
  level!: Level;
  debug: boolean = GAME.debugDefault;
  private blink = 0;
  private audioUnlocked = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private stage: HTMLElement,
    overlayRoot: HTMLElement,
    touchRoot: HTMLElement,
    private levels: LevelDef[],
  ) {
    for (const def of levels) {
      const errors = validateLevel(def);
      if (errors.length) console.warn(`level ${def.id} problems:`, errors);
    }

    this.renderer = new Renderer(canvas, buildSprites());
    this.input.bindings = this.save.data.bindings;
    this.input.attach(window);
    this.input.onRawKey = (code) => {
      if (code === 'F3') this.debug = !this.debug;
    };

    this.overlay = new Overlay(overlayRoot, {
      start: () => this.startGame(),
      resume: () => this.setPaused(false),
      restartLevel: () => {
        this.setPaused(false);
        this.loadLevel(this.levelIndex);
      },
      quit: () => this.toTitle(),
      retry: () => this.startGame(),
      next: () => this.advanceLevel(),
      openSettings: () => {
        this.state = 'settings';
        this.overlay.show('settings');
      },
      closeSettings: () => {
        const back: Screen = this.overlay.settingsReturn;
        if (back === 'pause') {
          this.state = 'pause';
          this.overlay.show('pause');
        } else {
          this.toTitle();
        }
      },
      clearSave: () => {
        this.save.clear();
        this.input.bindings = this.save.data.bindings;
        this.applyOptions();
        this.overlay.bindOptions(this.save.data.options);
        this.overlay.renderBindings(this.input.bindings);
        this.overlay.setText('hiscore', pad(this.save.data.hiscore, 6));
      },
      resetBinds: () => {
        this.save.data.bindings = cloneBindings(DEFAULT_BINDINGS);
        this.input.bindings = this.save.data.bindings;
        this.save.save();
        this.overlay.renderBindings(this.input.bindings);
      },
      optionChanged: (key, value) => this.onOptionChanged(key, value),
      rebind: (action, done) => this.rebind(action, done),
    });

    this.touch = new TouchControls(touchRoot, this.input, () => {
      if (this.state === 'play') this.setPaused(true);
      else if (this.state === 'pause') this.setPaused(false);
    });

    this.loop = new Loop({
      beginFrame: () => this.beginFrame(),
      update: (dt, step) => this.update(dt, step),
      render: () => this.render(),
    });

    window.addEventListener('resize', () => this.layout());
    window.addEventListener('orientationchange', () => this.layout());
    window.addEventListener('blur', () => {
      if (this.state === 'play') this.setPaused(true);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.state === 'play') this.setPaused(true);
    });
    const unlock = () => void this.unlockAudio();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    this.applyOptions();
    this.overlay.bindOptions(this.save.data.options);
    this.overlay.renderBindings(this.input.bindings);
    this.overlay.setText('hiscore', pad(this.save.data.hiscore, 6));
    this.loadLevel(0);
    this.layout();
    this.toTitle();
  }

  get options(): Options {
    return this.save.data.options;
  }

  run(): void {
    this.loop.start();
  }

  /* ------------------------------------------------------------- plumbing */

  private async unlockAudio(): Promise<void> {
    if (this.audioUnlocked) return;
    this.audioUnlocked = true;
    await this.audio.unlock();
    this.applyOptions();
  }

  private levelOptions(): LevelOptions {
    return {
      checkpoints: this.options.checkpoints,
      backtrack: this.options.backtrack,
      debug: this.debug,
    };
  }

  private applyOptions(): void {
    const o = this.options;
    this.audio.setMusicVolume(o.musicVol / 100, o.musicOn);
    this.audio.setSfxVolume(o.sfxVol / 100, o.sfxOn);
    const showTouch = o.touchMode === 'on' || (o.touchMode === 'auto' && TouchControls.isTouchDevice());
    this.touch.setVisible(showTouch);
    this.layout();
  }

  private onOptionChanged(key: keyof Options, value: unknown): void {
    (this.options as Record<string, unknown>)[key] = value;
    this.save.save();
    this.applyOptions();
    if (this.level) {
      this.level.options = this.levelOptions();
      this.level.camera.allowBacktrack = this.options.backtrack;
    }
  }

  private rebind(action: Action, done: () => void): void {
    this.input.captureKey((code) => {
      this.save.data.bindings[action] = [code];
      this.input.bindings = this.save.data.bindings;
      this.save.save();
      done();
    });
  }

  layout(): void {
    const pad = 8;
    const availW = Math.max(160, window.innerWidth - pad);
    const availH = Math.max(120, window.innerHeight - pad);
    let scale: number;
    if (this.options.scaleMode === 'fit') {
      scale = Math.max(1, Math.min(Math.floor(availW / VIEW_W), Math.floor(availH / VIEW_H)));
    } else {
      scale = Number(this.options.scaleMode) || 2;
      while (scale > 1 && (VIEW_W * scale > availW || VIEW_H * scale > availH)) scale--;
    }
    const w = VIEW_W * scale;
    const h = VIEW_H * scale;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.stage.style.width = `${w}px`;
    this.stage.style.height = `${h}px`;
  }

  /* ---------------------------------------------------------------- flow */

  private loadLevel(index: number): void {
    this.levelIndex = index;
    const def = this.levels[index];
    this.level = new Level(def, this.audio, this.input, this.session, this.levelOptions());
  }

  toTitle(): void {
    this.state = 'title';
    this.audio.setPaused(false);
    this.audio.stopMusic();
    this.session = { score: 0, coins: 0, lives: PLAYER.startLives };
    this.loadLevel(0);
    this.overlay.setText('hiscore', pad(this.save.data.hiscore, 6));
    this.overlay.show('title');
  }

  private startGame(): void {
    void this.unlockAudio();
    this.session = { score: 0, coins: 0, lives: PLAYER.startLives };
    this.loadLevel(0);
    this.state = 'play';
    this.overlay.show('none');
    this.audio.setPaused(false);
  }

  private advanceLevel(): void {
    const next = this.levelIndex + 1;
    if (next >= this.levels.length) {
      this.save.submitScore(this.session.score);
      this.overlay.setGameOver('ALL CLEAR — 全部通关！', this.session.score, this.save.data.hiscore);
      this.state = 'gameover';
      this.overlay.show('gameover');
      this.audio.playJingle('CLEAR', null);
      return;
    }
    this.loadLevel(next);
    this.state = 'play';
    this.overlay.show('none');
  }

  setPaused(paused: boolean): void {
    if (paused) {
      if (this.state !== 'play') return;
      this.state = 'pause';
      this.overlay.show('pause');
      this.audio.setPaused(true);
      this.audio.play('pause');
      this.input.releaseAll();
    } else {
      if (this.state !== 'pause' && this.state !== 'settings') return;
      this.state = 'play';
      this.overlay.show('none');
      this.audio.setPaused(false);
      this.loop.resetClock();
    }
  }

  private gameOver(): void {
    this.state = 'gameover';
    this.save.submitScore(this.session.score);
    const title = this.level.diedFromTime ? 'TIME UP — 时间到' : 'GAME OVER';
    this.overlay.setGameOver(title, this.session.score, this.save.data.hiscore);
    this.overlay.show('gameover');
    this.audio.playJingle('GAMEOVER', null);
  }

  private levelCleared(): void {
    const res = this.level.result;
    if (!res) return;
    this.state = 'clear';
    this.save.submitScore(this.session.score);
    this.overlay.setClear(`${res.levelName} CLEAR!`, [
      ['旗杆奖励 FLAG', String(res.flagBonus)],
      ['剩余时间 TIME', String(res.timeLeft)],
      ['时间奖励 BONUS', String(res.timeBonus)],
      ['收集 SUNBITS', String(res.coins)],
      ['总分 SCORE', String(this.session.score)],
    ]);
    this.overlay.show('clear');
  }

  /* --------------------------------------------------------------- frame */

  private beginFrame(): void {
    this.input.poll();

    if (this.input.pressed('pause')) {
      if (this.state === 'play') this.setPaused(true);
      else if (this.state === 'pause') this.setPaused(false);
    }
    if (this.input.pressed('restart')) {
      if (this.state === 'gameover') this.startGame();
      else if (this.state === 'clear') this.advanceLevel();
      else if (this.state === 'play' && this.level.phase === 'dead') {
        this.level.respawnPending = true;
      }
    }
    if (this.state === 'title' && (this.input.jumpPressed() || this.input.pressed('run'))) {
      this.startGame();
    }
  }

  private update(dt: number, step: number): void {
    this.blink += dt;
    if (this.state === 'play') {
      this.level.options = this.levelOptions();
      this.level.update(dt);
      if (this.level.respawnPending) {
        this.level.respawnPending = false;
        this.session.lives--;
        if (this.session.lives <= 0) this.gameOver();
        else this.level.restart(true);
      } else if (this.level.result) {
        this.levelCleared();
      }
    } else if (this.state === 'title') {
      // idle scenery: keep particles/animation ticking without gameplay
      this.level.particles.update(dt);
    }
    if (step === 0) this.input.consumeEdges();
  }

  private render(): void {
    this.level.draw(this.renderer);
    if (this.state !== 'title') {
      this.hud.draw(this.renderer, this.level, this.session, this.levels[this.levelIndex].id, this.blink);
      if (this.debug) {
        this.hud.drawDebug(this.renderer, this.level, { fps: this.loop.fps, steps: this.loop.steps });
      }
    } else {
      this.renderer.fade(0.25, '#1b1630');
      this.renderer.textShadow('SUNRISE VALE', VIEW_W / 2, 24, '#f5c33b', 'center');
      this.renderer.textShadow('PRESS START', VIEW_W / 2, VIEW_H - 30, '#f6f1e0', 'center');
    }
    if (this.state === 'pause' || this.state === 'settings') this.renderer.fade(0.35, '#0d0a16');
  }
}

function pad(v: number, n: number): string {
  return Math.max(0, Math.floor(v)).toString().padStart(n, '0');
}
